"use client"

import * as React from "react"
import { ContextMenu, ContextMenuContent, ContextMenuPortal, ContextMenuTrigger } from "../shadcn/context-menu"

interface TContextMenuProps {
	/** 우클릭 대상 영역. ContextMenuTrigger 내부에 렌더링된다 */
	children: React.ReactNode
	/**
	 * 메뉴 콘텐츠 렌더러.
	 * onClose는 onMenuClose 콜백만 호출하며 Radix 메뉴 자체의 닫힘은 트리거하지 못한다
	 * (Radix ContextMenu가 controlled open을 지원하지 않음).
	 * 메뉴 아이템 onSelect는 Radix가 자동으로 메뉴를 닫으므로 대부분의 경우 onClose는 필요 없다.
	 * 외부 상태 동기화 목적으로만 사용하라.
	 */
	renderMenu: (props: { onClose: () => void }) => React.ReactNode
	/** 메뉴 열림 콜백 */
	onMenuOpen?: () => void
	/** 메뉴 닫힘 콜백 */
	onMenuClose?: () => void
	/**
	 * Portal 사용 여부. 기본 true.
	 * false인 경우 parent stacking context에 갇히므로 z-index가 보장되지 않는다.
	 * 부모가 `overflow: hidden`이거나 낮은 z-index 컨테이너 내부면 반드시 true로 둬라.
	 */
	portal?: boolean
	/** Content className override */
	className?: string
}

export const TContextMenu = React.memo((props: TContextMenuProps) => {
	const { children, renderMenu, onMenuOpen, onMenuClose, portal = true, className } = props

	// Radix ContextMenu.Root는 controlled open을 지원하지 않는다.
	// onOpenChange 콜백으로 외부에 열림/닫힘을 알린다.
	const onOpenChange = React.useCallback(
		(open: boolean) => {
			if (open) {
				onMenuOpen?.()
			} else {
				onMenuClose?.()
			}
		},
		[onMenuOpen, onMenuClose]
	)

	// Radix ContextMenu는 controlled open을 지원하지 않으므로
	// onClose는 외부 콜백 호출만 담당한다. 실제 닫힘은 Radix가 처리한다.
	const handleClose = React.useCallback(() => {
		onMenuClose?.()
	}, [onMenuClose])

	const content = <ContextMenuContent className={className}>{renderMenu({ onClose: handleClose })}</ContextMenuContent>

	return (
		<ContextMenu onOpenChange={onOpenChange}>
			<ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
			{portal ? <ContextMenuPortal>{content}</ContextMenuPortal> : content}
		</ContextMenu>
	)
})

TContextMenu.displayName = "TContextMenu"
